<template>
	<div v-if="formVisible">
		<p>
			{{ $t('update-required-data.text') }}
		</p>
		<base-select v-if="employeeGroupVisible" v-model="$v.employeeGroup.$model"
			:options="$store.state.session.user.companyCode.employeeGroup" valueProp="id" label="groupName"
			:placeholder="$t('signupOH.center-or-business-unit')" :state="fieldIsValid('employeeGroup')"
			:errorMsg="$t('signupOH.errors.center-or-business-unit')">
		</base-select>
		<b-form-group v-if="employeeNumberVisible">
			<b-form-input v-model="$v.employeeNumber.$model" :state="employeeNumberState"
				:placeholder="$t('signupOH.internal-code')"></b-form-input>
			<b-form-invalid-feedback v-if="employeeNumberIsValid === false">{{ $t('signupOH.errors.employee-number')
				}}</b-form-invalid-feedback>
			<b-form-invalid-feedback
				v-if="$v.employeeNumber.$dirty && $v.employeeNumber.$invalid && employeeNumberIsValid !== false">
				{{ $t('signupOH.errors.required-employee-number') }}
			</b-form-invalid-feedback>
			<div class="alert alert-light mt-3 d-flex align-items-center">
				<span class="flex-grow-1" style="color: black;">
					{{ $t('update-required-data.contact-chat') }}
				</span>
				<i class="mdi mdi-chat-outline" style="color: black;"></i>
			</div>

		</b-form-group>
		<b-form-group v-if="dniVisible">
			<b-form-input v-model="$v.dni.$model" :state="dniState" :placeholder="$t('signupOH.registerNIF')" />
			<!-- Error servidor: NIF ya existe -->
			<b-form-invalid-feedback v-if="dniIsValid === false">
				{{ $t('update-required-data.messages.dni-already-exists') }}
			</b-form-invalid-feedback>

			<!-- Error local: campo vacío o formato incorrecto -->
			<b-form-invalid-feedback v-if="$v.dni.$dirty && $v.dni.$invalid && dniIsValid !== false">
				{{ $t('signupOH.errors.enterNIF') }}
			</b-form-invalid-feedback>
		</b-form-group>

		<div v-if="birthdateVisible">
			<base-birthdate-input minorsAllowed v-model="birthdate"></base-birthdate-input>
		</div>
		<base-button @click="submit" blue class="mt-3">{{ $t('update-required-data.save') }}</base-button>
	</div>
</template>

<script>
import LOGIN_REQUIRED_FIELDS from '@/constants/loginRequiredFields'
import {
	BFormGroup,
	BFormInput,
	BFormInvalidFeedback
} from 'bootstrap-vue'
import BaseBirthdateInput from '@/components/base/forms/BaseBirthdateInput.vue'
import {
	requiredIf,
	numeric
} from 'vuelidate/lib/validators'
import {
	validationsMixin
} from '@/mixins/validations.mixin'
import PatientsRepository from '@/repositories/PatientsRepository'
import accountMixin from '@/mixins/account.mixin'
import {
	calculateAge
} from '@/helpers/date.helpers'

export default {
	mixins: [validationsMixin, accountMixin],
	components: {
		BFormGroup,
		BFormInput,
		BaseBirthdateInput,
		BFormInvalidFeedback
	},
	data () {
		return {
			employeeGroup: null, // Selected employee group
			employeeNumber: null, // Employee number input value
			dni: null, // DNI input value
			birthdate: null,
			employeeNumberIsValid: null,
			dniIsValid: null
		}
	},
	watch: {
		employeeNumber () {
			// Cada vez que el usuario cambie el employeeNumber,
			// borramos el error de servidor para que pueda volver a validarse.
			this.employeeNumberIsValid = null
		},
		dni () {
			// Lo mismo para el dni
			this.dniIsValid = null
		}
	},

	validations: {
		employeeGroup: {
			required: requiredIf(function () {
				return this.employeeGroupVisible
			}),
			numeric
		},
		employeeNumber: {
			required: requiredIf(function () {
				return this.employeeNumberVisible
			})
		},
		dni: {
			required: requiredIf(function () {
				return this.dniVisible
			}),
			validateNIF (dni) {
				return !this.dniVisible || this.$validators.validatePersonalId(dni, this.$store.state.session.user.country)
			}
		},
		birthdate: {
			validateBirthdate (birthDate) {
				return birthDate !== null || !this.birthdateVisible
			}
		}
	},
	computed: {
		employeeGroupVisible () {
			return this.$store.state.session.requiredLoginFields.includes(LOGIN_REQUIRED_FIELDS.EMPLOYEE_GROUP_FIELD)
		},
		employeeNumberVisible () {
			return this.$store.state.session.requiredLoginFields.includes(LOGIN_REQUIRED_FIELDS.EMPLOYEE_NUMBER_FIELD)
		},
		dniVisible () {
			return this.$store.state.session.requiredLoginFields.includes(LOGIN_REQUIRED_FIELDS.DNI_FIELD)
		},
		birthdateVisible () {
			return this.$store.state.session.requiredLoginFields.includes(LOGIN_REQUIRED_FIELDS.BIRTHDATE_FIELD)
		},
		formVisible () {
			return this.employeeGroupVisible || this.employeeNumberVisible || this.dniVisible || this.birthdateVisible
		},
		form () {
			return {
				employeeGroup: this.$v.employeeGroup.$model,
				employeeNumber: this.$v.employeeNumber.$model,
				dni: this.$v.dni.$model,
				birthdate: this.birthdate
			}
		},
		employeeNumberState () {
			// Si hubo error de servidor, forzamos que sea inválido
			if (this.employeeNumberIsValid === false) return false

			// Caso contrario, seguimos la lógica de Vuelidate
			if (this.$v.employeeNumber.$dirty && this.$v.employeeNumber.$invalid) {
			return false
			}
			if (this.$v.employeeNumber.$dirty && !this.$v.employeeNumber.$invalid) {
			return true
			}
			// Si no está “tocada” la validación (no $dirty), no marcamos nada
			return null
		},
		dniState () {
			if (this.dniIsValid === false) return false

			// Vuelidate local
			if (this.$v.dni.$dirty && this.$v.dni.$invalid) {
			return false
			}
			if (this.$v.dni.$dirty && !this.$v.dni.$invalid) {
			return true
			}
			return null
		}
	},
	methods: {
		submit () {
			if (!this.fieldsAreValid()) return
			PatientsRepository.updatePatient(this.form).then(async (response) => {
					try {
						if (!response.data) {
							throw response.response.data.errorMessage
						}
						if (response.data.status === 200) {
							await this.defaultLoginCallback()
							if (this.birthdate) {
								const age = calculateAge(this.birthdate)
								this.$store.commit('session/setUserAge', age)
							}
						} else {
							throw response.data.errorMessage
						}
					} catch (error) {
						this.processError(error)
					}
				})
				.catch((error) => {
					this.processError(error)
				})
		},
		processError (error) {
			if (error.includes('dni')) {
				this.$store.dispatch('showDangerAlert', this.$t('update-required-data.messages.dni-already-exists'))
				this.dniIsValid = false
			} else if (error.includes('EMPLOYEE_NUMBER_REGISTERED')) {
				this.$store.dispatch('showDangerAlert', this.$t('error-checkemployeenumber'))
				this.employeeNumberIsValid = false
			} else if (error.includes('Birthdate')) {
				this.$store.dispatch('showDangerAlert', this.$t('update-required-data.messages.user-is-underage'))
			}
		}
	},
	async mounted () {
		if (!this.formVisible) {
			await this.defaultLoginCallback()
		}
	}
	}
</script>


// WEBPACK FOOTER //
// src/components/views/account/UpdateRequiredData.vue